// ServiceCallDetailsModal.js

import React, { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import DateFormat from './DateFormat';

const ServiceCallDetailsModal = ({
  isOpen,
  onClose,
  serviceCall,
  onEdit,
}) => {
 
   const [showFullTech, setShowFullTech] = useState(false);
   const [showFullNotes, setShowFullNotes] = useState(false);
 
   const techRaw = serviceCall?.TECHNOTES || '';
   const notesRaw = serviceCall?.NOTES || '';
 
   const TECH_LIMIT = 200;
   const NOTES_LIMIT = 200;
 
   const techNeedsTruncate = techRaw.length > TECH_LIMIT;
   const notesNeedsTruncate = notesRaw.length > NOTES_LIMIT;
 
   const techVisible = techNeedsTruncate && !showFullTech
     ? techRaw.slice(0, TECH_LIMIT) + '…'
     : techRaw;
 
   const notesVisible = notesNeedsTruncate && !showFullNotes
     ? notesRaw.slice(0, NOTES_LIMIT) + '…'
     : notesRaw;
 
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-20" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center p-4 bg-gray-900/30 backdrop-blur-sm">
              <Dialog.Panel className="mx-auto w-full max-w-2xl overflow-hidden rounded-2xl bg-white/90 backdrop-blur-md shadow-xl ring-1 ring-gray-200">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                  <Dialog.Title className="text-xl font-semibold text-gray-900">
                    Service Call Details
                  </Dialog.Title>

                  {serviceCall?.STATUS && (
                    <span
                      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset
                      ${
                        String(serviceCall.STATUS).toLowerCase() === 'completed'
                          ? 'bg-green-50 text-green-700 ring-green-600/20'
                          : 'bg-indigo-50 text-indigo-700 ring-indigo-600/20'
                      }`}
                    >
                      {serviceCall.STATUS}
                    </span>
                  )}                
                </div>

                {/* Body */}
                {serviceCall && (
                  <div className="px-6 py-5">
                {/* Tentative alert */}
                  {serviceCall?.STATUS === 'tentative' && (
                    <div className="mb-5 rounded-xl border border-red-200 bg-red-50 p-4">
                      <p className="text-sm font-semibold text-red-700">Tentative</p>
                      <p className="mt-1 text-sm text-red-700/90">
                        {serviceCall?.status_description || 'No description provided.'}
                      </p>
                    </div>
                  )}                    
                    <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="rounded-lg bg-gray-50 p-4">
                        <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Log</dt>
                        <dd className="mt-1 text-sm font-medium text-gray-900">{serviceCall.id ?? '—'}</dd>
                      </div>
                      <div className="rounded-lg bg-gray-50 p-4">
                        <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Logged</dt>
                        <dd className="mt-1 text-sm font-medium text-gray-900">{serviceCall.USER ?? '—'}</dd>
                      </div>

                      <div className="rounded-lg bg-gray-50 p-4 sm:col-span-2">
                        <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Trouble</dt>
                        <dd className="mt-1 whitespace-pre-wrap text-sm text-gray-900">{serviceCall.TROUBLE ?? '—'}</dd>
                      </div>

                      <div className="rounded-lg bg-gray-50 p-4">
                        <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Customer</dt>
                        <dd className="mt-1 text-sm font-medium text-gray-900">{serviceCall.CUSTOMER ?? '—'}</dd>
                      </div>

                      <div className="rounded-lg bg-gray-50 p-4">
                        <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Address</dt>
                        <dd className="mt-1 text-sm font-medium text-gray-900">{serviceCall.ADDRESS ?? '—'}</dd>
                      </div>

                      <div className="rounded-lg bg-gray-50 p-4">
                        <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                          Called By / Phone
                        </dt>
                        <dd className="mt-1 text-sm font-medium text-gray-900">
                          {serviceCall.CALLED_BY ?? '—'} 
                          <span className="text-gray-400 mx-2">|</span> 
                          {serviceCall.PHONE ?? '—'}
                        </dd>
                      </div>


                      <div className="rounded-lg bg-gray-50 p-4">
                        <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Technician</dt>
                        <dd className="mt-1 text-sm font-medium text-gray-900">{serviceCall.TECH ?? '—'}</dd>
                      </div>

                      {Array.isArray(serviceCall.add_tech) && serviceCall.add_tech.length > 0 && (
                        <div className="rounded-lg bg-gray-50 p-4">
                          <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Additional Tech(s)</dt>
                          <dd className="mt-1 space-y-1 text-sm font-medium text-gray-900">
                            {serviceCall.add_tech.map((t) => (
                              <div key={t.id}>{t.tech_name}</div>
                            ))}
                          </dd>
                        </div>
                      )}

                      <div className="rounded-lg bg-gray-50 p-4">
                        <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Time In/Out</dt>
                        <dd className="mt-1 text-sm font-medium text-gray-900">{serviceCall.TIME || '—'} - {serviceCall.end_time || '—'}</dd>
                      </div>

                      <div className="rounded-lg bg-gray-50 p-4">
                        <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Schedule Date</dt>
                        <dd className="mt-1 text-sm font-medium text-gray-900">
                          {serviceCall.SCHEDULE_DATE ? <DateFormat date={serviceCall.SCHEDULE_DATE} /> : '—'}
                        </dd>
                      </div>

                      <div className="rounded-lg bg-green-100 p-4 sm:col-span-2">
                        <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Tech Notes</dt>
                        <dd className="mt-1 whitespace-pre-wrap text-sm text-gray-900">
                          {techVisible || '—'}
                        </dd>

                        {techNeedsTruncate && (
                          <div className="mt-2">
                            <button
                              type="button"
                              onClick={() => setShowFullTech((v) => !v)}
                              className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded px-1"
                              aria-expanded={showFullTech}
                            >
                              {showFullTech ? 'Show less' : 'Show more'}
                            </button>
                          </div>
                        )}
                      </div>

                      <div className="rounded-lg bg-gray-50 p-4 sm:col-span-2">
                        <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Notes</dt>
                        <dd className="mt-1 whitespace-pre-wrap text-sm text-gray-900">
                          {notesVisible || '—'}
                        </dd>

                        {notesNeedsTruncate && (
                          <div className="mt-2">
                            <button
                              type="button"
                              onClick={() => setShowFullNotes((v) => !v)}
                              className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded px-1"
                              aria-expanded={showFullNotes}
                            >
                              {showFullNotes ? 'Show less' : 'Show more'}
                            </button>
                          </div>
                        )}
                      </div>

                    </dl>
                  </div>
                )}

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 border-t border-gray-200 bg-gray-50 px-6 py-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={onEdit}
                    className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    Edit
                  </button>
                </div>
              </Dialog.Panel>
            </div>
          </div>
        </Transition.Child>
      </Dialog>
    </Transition>
  );
};

export default ServiceCallDetailsModal;
